import React, { useState, useEffect } from "react";
import { X, ChevronDown, ChevronUp, IndianRupee, Link2, Image, Upload, Gift } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createNewClass } from "./profileAPIs";
import toast, { Toaster } from "react-hot-toast";
import { parseIsoDate, toIsoDate } from "../../utils/date";

interface AddClassFormProps {
    show: boolean;
    onClose: () => void;
    onCreated?: () => Promise<void> | void;
}

const classCategories = [
    "Hatha Yoga", "Vinyasa Flow", "Ashtanga Yoga", "Yin Yoga", "Hot Yoga",
    "Restorative Yoga", "Kundalini Yoga", "Power Yoga", "Prenatal Yoga", "Meditation & Mindfulness", "Others"
];

interface classCreated {
    success: boolean;
    message?: string;
}

const AddClassForm: React.FC<AddClassFormProps> = ({ show, onClose, onCreated }) => {
    const [formVisible, setFormVisible] = useState(true);
    const [dayDifference, setDayDifference] = useState<number | null>(null);
    const [schedule, setSchedule] = useState({
        sunday: { isActive: false },
        monday: { isActive: false },
        tuesday: { isActive: false },
        wednesday: { isActive: false },
        thursday: { isActive: false },
        friday: { isActive: false },
        saturday: { isActive: false },
    });
    const [formData, setFormData] = useState({
        title: "",
        duration: "",
        durationUnit: "mins",
        category: "",
        description: "",
        startDate: "",
        endDate: "",
        pricingType: "paid", // "paid", or "demo"
        demoDuration: "1",
        prices: {
            session: "",
            monthly: "",
            yearly: ""
        },
        googleMeetLinks: [
            { id: crypto.randomUUID(), url: "", title: "", time: "", period: "AM" }
        ],
        thumbnail: null as File | null,
        thumbnailPreview: ""
    });
    const [isLoading, setLoading] = useState(false);

    const getMinDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const minDate = getMinDate();

    const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                toast.error("Only JPEG, PNG, and JPG formats are accepted.");
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                toast.error("File size should be less than 5MB");
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                setFormData((prev: any) => ({
                    ...prev,
                    thumbnail: file,
                    thumbnailPreview: reader.result as string
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const removeThumbnail = () => {
        setFormData((prev: any) => ({
            ...prev,
            thumbnail: null,
            thumbnailPreview: ""
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name.startsWith("price-")) {
            const key = name.replace("price-", "") as "session" | "monthly" | "yearly";
            if (parseFloat(value) < 0) {
                return;
            }
            setFormData(prev => ({
                ...prev,
                prices: { ...prev.prices, [key]: value }
            }));
        } else if (name === "duration") {
            const duration = parseInt(value, 10);
            if (duration < 0) {
                return;
            }
            // Convert to minutes if needed for validation
            const durationInMinutes = formData.durationUnit === "hours" ? duration * 60 : duration;
            const maxDurationInMinutes = 3 * 60; // 3 hours max
            
            if (durationInMinutes > maxDurationInMinutes) {
                toast.error("Maximum class duration is 3 hours.");
                return;
            }
            setFormData(prev => ({ ...prev, [name]: value }));
        } else if (name === "demoDuration") {
            if (parseInt(value, 10) < 0) {
                return;
            }
            setFormData(prev => ({ ...prev, [name]: value }));
        } else if (name === "durationUnit") {
            // Validate existing duration when unit changes
            const duration = parseInt(formData.duration, 10);
            const durationInMinutes = value === "hours" ? duration * 60 : duration;
            const maxDurationInMinutes = 3 * 60; // 3 hours max
            
            if (durationInMinutes > maxDurationInMinutes) {
                toast.error("Maximum class duration is 3 hours.");
                return;
            }
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleScheduleChange = (day: string, value: any) => {
        setSchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day as keyof typeof prev],
                isActive: value
            }
        }));
    };

    const validateGoogleMeetLinks = () => {
        if (formData.googleMeetLinks.length === 0) {
            toast.error("At least one Google Meet session is required.");
            return false;
        }

        for (const link of formData.googleMeetLinks) {
            if (!link.url.trim() || !link.title.trim() || !link.time.trim()) {
                toast.error("Please fill all fields for each Google Meet session.");
                return false;
            }

            const timeParts = link.time.split(':');
            if (timeParts.length !== 2) {
                toast.error(`Invalid time format for session "${link.title}". Please use HH:MM.`);
                return false;
            }

            const hours = parseInt(timeParts[0], 10);
            const minutes = parseInt(timeParts[1], 10);

            if (isNaN(hours) || isNaN(minutes) || hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
                toast.error(`Invalid time for session "${link.title}". Hours must be between 01-12 and minutes between 00-59.`);
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!formData.thumbnail) {
            toast.error("Thumbnail is required.");
            return;
        }
        if (!formData.title.trim()) {
            toast.error("Class Title is required.");
            return;
        }
        if (!formData.duration.trim()) {
            toast.error("Class Duration is required.");
            return;
        }
        // Validate maximum duration (3 hours)
        const duration = parseInt(formData.duration, 10);
        const durationInMinutes = formData.durationUnit === "hours" ? duration * 60 : duration;
        const maxDurationInMinutes = 3 * 60; // 3 hours max
        if (durationInMinutes > maxDurationInMinutes) {
            toast.error("Maximum class duration is 3 hours.");
            return;
        }
        if (!formData.category) {
            toast.error("Class Category is required.");
            return;
        }
        if (!formData.description.trim()) {
            toast.error("Class Description is required.");
            return;
        }
        if (!formData.startDate) {
            toast.error("Start Date is required.");
            return;
        }
        if (formData.startDate < minDate) {
            toast.error("Start Date cannot be in the past.");
            return;
        }
        if (!formData.endDate) {
            toast.error("End Date is required.");
            return;
        }
        if (formData.endDate < formData.startDate) {
            toast.error("End Date cannot be before Start Date.");
            return;
        }
        if (formData.pricingType !== 'free' && !formData.prices.session.trim()) {
            toast.error("Session Price is required for paid and demo classes.");
            return;
        }
        
        if (!validateGoogleMeetLinks()) {
            return;
        }

        const payloadStartDate = toIsoDate(formData.startDate);
        const payloadEndDate = toIsoDate(formData.endDate);

        const data = new FormData();
        data.append("classTitle", formData.title);
        data.append("classDuration", `${formData.duration} ${formData.durationUnit}`);
        data.append("classCategory", formData.category);
        data.append("classDescription", formData.description);
        data.append("startDate", payloadStartDate);
        data.append("endDate", payloadEndDate);
        data.append("pricingType", formData.pricingType);
        console.log("Submitting startDate:", payloadStartDate);
        console.log("Submitting endDate:", payloadEndDate);
        
        if (formData.pricingType === "demo") {
            data.append("demoDuration", formData.demoDuration);
        }

        if (formData.pricingType === "paid" || formData.pricingType === "demo") {
            data.append("prices", JSON.stringify(formData.prices));
        } else {
            data.append("prices", JSON.stringify({ session: "0", monthly: "0", yearly: "0" }));
        }

        if (formData.thumbnail) {
            data.append("thumbnail", formData.thumbnail);
        }

        if (formData.prices.session) {
            data.append("schedule", JSON.stringify(schedule));
        }

        const meetLinks = formData.googleMeetLinks.map(({ id, url, title, time, period }) => ({
            id,
            url,
            title,
            time: `${time} ${period}`
        }));
        data.append("googleMeetLinks", JSON.stringify(meetLinks));

        try {
            setLoading(true);
            const res = await createNewClass(data) as classCreated;
            if (res.success) {
                toast.success(res.message || "Class created successfully!");
                await onCreated?.();
                onClose();
            } else {
                toast.error(res.message || "Failed to create class. Please try again.");
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("An error occurred while creating the class.");
            }
        }
    };

    const handleTimeInputChange = (id: string, value: string) => {
        const timeValue = value.replace(/[^0-9]/g, '').slice(0, 4);
        let formattedTime = timeValue;
        if (timeValue.length > 2) {
            formattedTime = `${timeValue.slice(0, 2)}:${timeValue.slice(2)}`;
        }
        
        setFormData(prev => ({
            ...prev,
            googleMeetLinks: prev.googleMeetLinks.map(link =>
                link.id === id ? { ...link, time: formattedTime } : link
            )
        }));
    };

    const handleGoogleLinkChange = (id: string, field: "url" | "title" | "period", value: string) => {
        setFormData(prev => ({
            ...prev,
            googleMeetLinks: prev.googleMeetLinks.map(link =>
                link.id === id ? { ...link, [field]: value } : link
            )
        }));
    };

    const addGoogleLink = () => {
        setFormData(prev => ({
            ...prev,
            googleMeetLinks: [
                ...prev.googleMeetLinks,
                { id: crypto.randomUUID(), url: "", title: "", time: "", period: "AM" }
            ]
        }));
    };

    const removeGoogleLink = (id: string) => {
        setFormData(prev => ({
            ...prev,
            googleMeetLinks: prev.googleMeetLinks.filter(link => link.id !== id)
        }));
    };

    useEffect(() => {
        if (formData.startDate && formData.endDate) {
            const start = parseIsoDate(formData.startDate);
            const end = parseIsoDate(formData.endDate);
            if (start && end && start < end) {
                const diffTime = Math.abs(end.getTime() - start.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                setDayDifference(diffDays);
            } else {
                setDayDifference(null);
            }
        } else {
            setDayDifference(null);
        }
    }, [formData.startDate, formData.endDate]);

    if (!show) return null;

    return (
        <div className="fixed overflow-y-auto inset-0 p-2 sm:p-6 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Toaster />
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-white p-4 md:p-6 rounded-2xl shadow-2xl relative">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                    <div> 
                        <h1 className="text-2xl sm:text-3xl font-bold text-purple-700">Create New Yoga Class</h1>
                        <p className="text-gray-500 text-sm">Design your class and pricing strategy</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-red-600 transition self-end sm:self-auto">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex justify-end mb-4">
                    <button
                        onClick={() => setFormVisible(prev => !prev)}
                        className="flex items-center text-purple-600 hover:text-purple-800 font-medium transition"
                    >
                        {formVisible ? <ChevronUp className="mr-1" /> : <ChevronDown className="mr-1" />}
                        {formVisible ? "Hide Form" : "Show Form"}
                    </button>
                </div>

                <AnimatePresence>
                    {formVisible && (
                        <motion.div
                            key="form-content"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.4 }}
                            className="overflow-hidden"
                        >
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Class Thumbnail Upload */}
                                <div className="mb-6 lg:col-span-2">
                                    <h2 className="text-lg font-semibold text-purple-700 flex items-center mb-2">
                                        <Image className="w-5 h-5 mr-2" />
                                        Class Thumbnail
                                    </h2>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                                        {!formData.thumbnailPreview ? (
                                            <label className="cursor-pointer group w-full sm:w-32 h-24 border-2 border-dashed border-purple-300 rounded-lg flex flex-col items-center justify-center text-purple-600 hover:bg-purple-50 transition-all">
                                                <Upload className="w-5 h-5 mb-1" />
                                                <span className="text-xs font-medium">Upload</span>
                                                <input
                                                    type="file"
                                                    accept=".jpeg,.png,.jpg"
                                                    onChange={handleThumbnailChange}
                                                    className="hidden"
                                                    required
                                                />
                                            </label>
                                        ) : (
                                            <div className="relative w-full sm:w-32 h-24">
                                                <img
                                                    src={formData.thumbnailPreview}
                                                    alt="Thumbnail Preview"
                                                    className="w-full h-full object-cover rounded-lg border shadow"
                                                />
                                                <button
                                                    onClick={removeThumbnail}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-sm text-gray-600">Upload a thumbnail image.</p>
                                            <p className="text-xs text-gray-400">This helps attract more students</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Class Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder="Class Title"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>

                                {/* Duration */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Class Duration</label>
                                    <div className="flex items-center">
                                        <input
                                            type="number"
                                            name="duration"
                                            value={formData.duration}
                                            onChange={handleChange}
                                            placeholder="e.g., 45"
                                            className="w-full p-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-purple-500 outline-none"
                                            required
                                        />
                                        <select
                                            name="durationUnit"
                                            value={formData.durationUnit}
                                            onChange={handleChange}
                                            className="p-3 border-t border-b border-r border-gray-300 rounded-r-lg bg-gray-50 focus:ring-2 focus:ring-purple-500 outline-none"
                                        >
                                            <option value="mins">mins</option>
                                            {/* <option value="hours">hours</option> */}
                                        </select>
                                    </div>
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Class Category</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        required
                                    >
                                        <option value="">Select Class Category</option>
                                        {classCategories.map((cat) => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Class Description</label>
                                    <textarea name="description" value={formData.description}
                                        onChange={handleChange} rows={4} placeholder="Describe your class..." className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none" required/>
                                </div>
                                
                                {/* Start Date */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                                    <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} min={minDate} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" required/>
                                </div>

                                {/* End Date */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                                    <input type="date" name="endDate" value={formData.endDate}
                                        onChange={handleChange} min={formData.startDate || minDate} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        required
                                    />
                                </div>

                                {/* Pricing Type Selection */}
                                <div className="lg:col-span-2">
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 shadow-sm">
                                        <h2 className="text-lg font-semibold text-green-700 mb-4 flex items-center">
                                            <IndianRupee className="w-5 h-5 mr-2" />
                                            Pricing Options
                                        </h2>
                                        
                                        <div className="flex items-center justify-center px-6 py-3 rounded-lg bg-green-600 text-white shadow-lg mb-4">
                                            <IndianRupee className="w-5 h-5 mr-2" />
                                            Paid Class
                                        </div>

                                        {/* Pricing Type Toggle */}
                                        <div className="flex items-center mb-4">
                                            <input
                                                type="checkbox"
                                                id="isDemo"
                                                checked={formData.pricingType === 'demo'}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        pricingType: isChecked ? 'demo' : 'paid',
                                                        demoDuration: isChecked ? prev.demoDuration : '1'
                                                    }));
                                                }}
                                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="isDemo" className="ml-2 text-sm font-medium text-gray-700">
                                                Add demo to this class
                                            </label>
                                        </div>

                                        {/* Pricing Inputs - Show for paid and demo classes */}
                                        {(formData.pricingType === "paid" || formData.pricingType === "demo") && (
                                            <div>
                                                {formData.pricingType === 'demo' && (
                                                    <div className="mb-6">
                                                        <label className="block text-sm font-semibold text-gray-700 mb-1">Demo Duration (in days)</label>
                                                        <input
                                                            type="number"
                                                            name="demoDuration"
                                                            value={formData.demoDuration}
                                                            onChange={handleChange}
                                                            placeholder="Number of demo days"
                                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                )}
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                                    <>
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-600 capitalize mb-1">
                                                                Session Price
                                                            </label>
                                                            <input
                                                                type="number"
                                                                name="price-session"
                                                                value={formData.prices.session}
                                                                onChange={handleChange}
                                                                placeholder="Price (₹)"
                                                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                                                required
                                                                min="0"
                                                            />
                                                        </div>
                                                        {dayDifference !== null && dayDifference >= 30 && (
                                                            <div>
                                                                <label className="block text-sm font-semibold text-gray-600 capitalize mb-1">
                                                                    Monthly Price (Optional)
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    name="price-monthly"
                                                                    value={formData.prices.monthly}
                                                                    onChange={handleChange}
                                                                    placeholder="Price (₹)"
                                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                                                    min="0"
                                                                />
                                                            </div>
                                                        )}
                                                        {dayDifference !== null && dayDifference >= 365 && (
                                                            <div>
                                                                <label className="block text-sm font-semibold text-gray-600 capitalize mb-1">
                                                                    Yearly Price (Optional)
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    name="price-yearly"
                                                                    value={formData.prices.yearly}
                                                                    onChange={handleChange}
                                                                    placeholder="Price (₹)"
                                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                                                    min="0"
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                </div>
                                            </div>
                                        )}

                                        {formData.pricingType === "free" && (
                                            <div className="text-center py-6">
                                                <div className='bg-green-100 text-green-700 inline-flex items-center px-6 py-3 rounded-lg'>
                                                    <Gift className="w-5 h-5 mr-2" />
                                                    <span className="font-medium">
                                                        This class will be completely free for all students
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        <p className="text-sm text-gray-500 mt-3">
                                            {formData.pricingType === "paid" 
                                                ? "Set prices for different subscription types. Leave fields empty if not offering that option."
                                                : "Free and demo classes are great for building your student base and showcasing your teaching style."
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="lg:col-span-2 mt-6">
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200 shadow-sm">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                                        <h2 className="text-lg font-semibold text-blue-700 flex items-center">
                                            <Link2 className="w-5 h-5 mr-2" />
                                            Google Meet Sessions
                                        </h2>
                                        <button
                                            type="button"
                                            onClick={addGoogleLink}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition self-start sm:self-auto"
                                        >
                                            + Add Session
                                        </button>
                                    </div>

                                    {formData.googleMeetLinks.length === 0 ? (
                                        <p className="text-center text-gray-500 py-6">No sessions added</p>
                                    ) : (
                                        formData.googleMeetLinks.map((link, index) => (
                                            <div key={link.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 bg-white p-4 rounded-lg shadow-sm border border-blue-100">
                                                <input
                                                    type="url"
                                                    value={link.url}
                                                    onChange={(e) => handleGoogleLinkChange(link.id, "url", e.target.value)}
                                                    placeholder="Google Meet Link"
                                                    className="p-3 border border-gray-300 rounded-lg"
                                                    required
                                                />
                                                <input
                                                    type="text"
                                                    value={link.title}
                                                    onChange={(e) => handleGoogleLinkChange(link.id, "title", e.target.value)}
                                                    placeholder="Session Title"
                                                    className="p-3 border border-gray-300 rounded-lg"
                                                    required
                                                />
                                                <div className="flex gap-2">
                                                    <div className="flex items-center border border-gray-300 rounded-lg p-3 w-full">
                                                        <input
                                                            type="text"
                                                            value={link.time}
                                                            onChange={(e) => handleTimeInputChange(link.id, e.target.value)}
                                                            placeholder="HH:MM"
                                                            className="outline-none w-full"
                                                            maxLength={5}
                                                            required
                                                        />
                                                        <select
                                                            value={(link as any).period}
                                                            onChange={(e) => handleGoogleLinkChange(link.id, "period", e.target.value)}
                                                            className="outline-none bg-transparent border-l pl-2 ml-2"
                                                        >
                                                            <option value="AM">AM</option>
                                                            <option value="PM">PM</option>
                                                        </select>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeGoogleLink(link.id)}
                                                        className="text-red-500 hover:text-red-700 px-2"
                                                    >
                                                        <X className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {formData.prices.session && (
                                <div className="lg:col-span-2 mt-6">
                                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border border-purple-200 shadow-sm">
                                        <h2 className="text-lg font-semibold text-purple-700 flex items-center mb-4">
                                            Available Days and Time
                                        </h2>
                                        <div className="space-y-4">
                                            {Object.keys(schedule).map(day => (
                                                <div key={day} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={schedule[day as keyof typeof schedule].isActive}
                                                        onChange={(e) => handleScheduleChange(day, e.target.checked)}
                                                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                                    />
                                                    <label className="ml-3 block text-sm font-medium text-gray-700 capitalize">
                                                        {day}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            <div className="mt-6 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className={`w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold px-8 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg ${isLoading ? "cursor-not-allowed" : ""}`}>
                                    {isLoading ? "Creating..." : "Create Class"}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default AddClassForm;
